import React, { useCallback, useId, useLayoutEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogTitle,
} from './pilot-ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogTitle,
} from './pilot-ui/alert-dialog';
import Button, { type ButtonVariant } from './ui/Button';
import ActionRow from './ui/ActionRow';
import { resolveReturnFocus } from './returnFocus';

export type ConfirmTone = 'primary' | 'danger';

interface ConfirmDialogBaseProps {
  open: boolean;
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string | null;
  confirmTitle?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  submitting?: boolean;
  allowCancelWhileSubmitting?: boolean;
  confirmDisabled?: boolean;
  disabledReason?: string;
  error?: string | null;
  deferredInitialFocus?: string;
  returnFocusTo?: React.RefObject<HTMLElement | null> | string | null;
  restoreOnUnmount?: boolean;
  onCancel: () => void;
  children?: React.ReactNode;
  maxWidth?: 'md' | 'lg' | '2xl' | '5xl';
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
}

export type ConfirmDialogProps = ConfirmDialogBaseProps & (
  | { onConfirm: () => void; footer?: never; footerReason?: never; footerReasonId?: never }
  // footerReason: why a button in the custom footer is disabled, shown in the
  // action row's reason area with the id footerReasonId.
  | { footer: React.ReactNode; footerReason?: string | false | null; footerReasonId?: string; onConfirm?: never }
);

const FOOTER_CLASS = 'relative z-10 shrink-0 border-t bg-background p-6';

const toneVariant: Record<ConfirmTone, ButtonVariant> = {
  primary: 'primary',
  danger: 'danger',
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  confirmTitle,
  cancelLabel = 'Cancel',
  tone = 'primary',
  submitting = false,
  allowCancelWhileSubmitting = false,
  confirmDisabled = false,
  disabledReason = 'Complete or change the required details before continuing.',
  error = null,
  deferredInitialFocus,
  returnFocusTo = null,
  restoreOnUnmount = false,
  onConfirm,
  onCancel,
  children,
  footer,
  footerReason,
  footerReasonId,
  maxWidth = 'md',
  onKeyDown,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const messageId = useId();
  const disabledReasonId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const deferredHandoffRef = useRef<{ initialized: boolean; pending: boolean; armed: boolean; fallback: HTMLElement | null }>({ initialized: false, pending: false, armed: false, fallback: null });
  const returnFocusRef = useRef(returnFocusTo);
  // A promise-backed confirmation clears its options as it closes. Keep the
  // last live target until Base UI has finished its exit focus handoff.
  if (open) returnFocusRef.current = returnFocusTo;

  const resolveCloseFocus = () => {
    const target = returnFocusRef.current;
    const preferred = typeof target === 'string'
      ? document.querySelector<HTMLElement>(target)
      : target?.current;
    return resolveReturnFocus(preferred, openerRef.current);
  };

  const available = (candidate: HTMLElement) => {
    if (candidate.matches(':disabled, [disabled], [aria-disabled="true"], [data-disabled]')) return false;
    for (let node: HTMLElement | null = candidate; node && node !== panelRef.current?.parentElement; node = node.parentElement) {
      if (node.hidden || node.inert || node.getAttribute('aria-hidden') === 'true') return false;
      const style = getComputedStyle(node);
      if (style.visibility === 'hidden' || style.display === 'none') return false;
    }
    return true;
  };
  const focusCandidate = () => {
    const fields = panelRef.current?.querySelectorAll<HTMLElement>(
      'input, select, textarea, [data-slot="select-trigger"], [data-slot="checkbox"]',
    );
    const field = Array.from(fields ?? []).find(available);
    if (field) return field;
    const deferredField = deferredInitialFocus
      ? panelRef.current?.querySelector<HTMLElement>(deferredInitialFocus)
      : null;
    if (deferredField && available(deferredField)) return deferredField;
    if (footer === undefined) {
      if (confirmRef.current && available(confirmRef.current)) return confirmRef.current;
      if (cancelRef.current && available(cancelRef.current)) return cancelRef.current;
    }
    const actions = footerRef.current?.querySelectorAll<HTMLElement>('button, [data-shared-button]');
    return Array.from(actions ?? []).find(available) ?? panelRef.current;
  };
  const deferredInitialFocusRef = useRef(deferredInitialFocus);
  deferredInitialFocusRef.current = deferredInitialFocus;
  const focusCandidateRef = useRef(focusCandidate);
  focusCandidateRef.current = focusCandidate;
  const initialFocus = useCallback(() => {
    const first = focusCandidateRef.current();
    const handoff = deferredHandoffRef.current;
    if (!handoff.initialized) {
      const selector = deferredInitialFocusRef.current;
      const preferred = selector ? panelRef.current?.querySelector<HTMLElement>(selector) : null;
      deferredHandoffRef.current = {
        initialized: true,
        pending: !!selector && first !== preferred,
        armed: false,
        fallback: first ?? null,
      };
      const opening = deferredHandoffRef.current;
      if (opening.pending) {
        requestAnimationFrame(() => {
          if (deferredHandoffRef.current === opening) opening.armed = true;
        });
      }
    } else if (document.activeElement instanceof HTMLElement && panelRef.current?.contains(document.activeElement)) {
      return document.activeElement;
    }
    return first;
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      deferredHandoffRef.current = { initialized: false, pending: false, armed: false, fallback: null };
      return;
    }
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }, [open]);

  const noteFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    const handoff = deferredHandoffRef.current;
    if (handoff.pending && handoff.armed && event.target !== handoff.fallback) handoff.pending = false;
  };

  // A delayed field can take initial focus once. Moving away from the fallback
  // or completing the handoff ends this opening's request.
  useLayoutEffect(() => {
    if (!open || !deferredInitialFocus || !deferredHandoffRef.current.pending) return;
    const frame = requestAnimationFrame(() => {
      const handoff = deferredHandoffRef.current;
      if (!handoff.pending) return;
      if (document.activeElement !== handoff.fallback) {
        handoff.pending = false;
        return;
      }
      const target = panelRef.current?.querySelector<HTMLElement>(deferredInitialFocus);
      if (target && available(target)) {
        handoff.pending = false;
        target.focus();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [open, deferredInitialFocus, children, footer, submitting, confirmDisabled]);

  // Callers can remove an editor as soon as Cancel is pressed. In that case
  // Base UI's close animation never runs, so its finalFocus callback cannot
  // restore focus. Resolve the same shared target after the removal commits.
  useLayoutEffect(() => {
    if (!open || !restoreOnUnmount) return;
    return () => {
      const focused = document.activeElement;
      if (focused !== document.body && !panelRef.current?.contains(focused)) return;
      requestAnimationFrame(() => {
        if (document.activeElement !== document.body) return;
        resolveCloseFocus()?.focus();
      });
    };
  }, [open, restoreOnUnmount]);

  const close = () => {
    if (!submitting) onCancel();
  };
  const finalFocus = resolveCloseFocus;

  const body = (
    <>
      <div className="shrink-0 px-6 pt-6">
        {tone === 'danger' ? <AlertDialogTitle className="text-xl font-semibold">{title}</AlertDialogTitle> : <DialogTitle className="text-xl font-semibold">{title}</DialogTitle>}
        {error && (
          <div className="mt-3 rounded-md border border-status-error-border bg-red-100 p-3 text-sm font-medium text-red-800" role="alert">
            {error}
          </div>
        )}
      </div>
      <div className="min-h-0 overflow-y-auto px-6 py-3">
        {message && <div id={messageId} className="mb-3 text-foreground">{message}</div>}
        {children && <div className="mb-3">{children}</div>}
      </div>
      {footer !== undefined ? (
        <ActionRow ref={footerRef} align="end" className={FOOTER_CLASS} reason={footerReason} reasonId={footerReasonId}>{footer}</ActionRow>
      ) : (
        <ActionRow
          ref={footerRef}
          align="end"
          className={FOOTER_CLASS}
          reason={confirmDisabled && disabledReason}
          reasonId={disabledReasonId}
        >
          <Button ref={cancelRef} variant="secondary" className="w-full sm:w-auto" onClick={onCancel} disabled={submitting && !allowCancelWhileSubmitting} title={confirmLabel === null ? `Close ${title}` : 'Close this without doing anything'}>
            {cancelLabel}
          </Button>
          {confirmLabel !== null && <Button
            ref={confirmRef}
            variant={toneVariant[tone]}
            onClick={onConfirm}
            disabled={submitting || confirmDisabled}
            aria-describedby={confirmDisabled ? disabledReasonId : undefined}
            title={confirmTitle ?? (typeof message === 'string' ? `${title} ${message}` : title)}
          >
            {submitting ? 'Working…' : confirmLabel}
          </Button>}
        </ActionRow>
      )}
    </>
  );

  const contentClass = `flex max-h-[90vh] w-full max-w-[calc(100%-2rem)] ${maxWidth === '5xl' ? 'sm:max-w-5xl' : maxWidth === '2xl' ? 'sm:max-w-2xl' : maxWidth === 'lg' ? 'sm:max-w-lg' : 'sm:max-w-md'} flex-col gap-0 overflow-hidden p-0`;
  if (tone === 'danger') {
    return (
      <AlertDialog open={open} onOpenChange={(next) => { if (!next) close(); }}>
        <AlertDialogContent
          ref={panelRef}
          onFocusCapture={noteFocus}
          customWidth
          className={contentClass}
          aria-busy={submitting || undefined}
          aria-describedby={message ? messageId : undefined}
          onBackdropClick={close}
          initialFocus={initialFocus}
          finalFocus={finalFocus}
          onKeyDown={onKeyDown}
        >
          {body}
        </AlertDialogContent>
      </AlertDialog>
    );
  }
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close(); }} disablePointerDismissal={submitting}>
      <DialogContent
        ref={panelRef}
        onFocusCapture={noteFocus}
        customWidth
        className={contentClass}
        showCloseButton={false}
        aria-busy={submitting || undefined}
        aria-describedby={message ? messageId : undefined}
        initialFocus={initialFocus}
        finalFocus={finalFocus}
        onKeyDown={onKeyDown}
      >
        {body}
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
